import { accountRepo } from '@ctct/domain-layer';
import { navigationApi } from '@ctct/infrastructure-layer';

import type { TranslationGetter } from '../../../types';
import { thankYouEventBus } from '../ThankYouEventBus';
import type { Init } from '../types';

export class ThankYouService {
  constructor(
    private t: TranslationGetter,
    private xeroPartnerTurnedOn: boolean,
    private isSocialStarter: boolean,
    private isCurrentPlanEvents: boolean,
    private hasVerifiedEmail: boolean,
    private version: Init['version'],
    private firstName?: string,
    private billingEmailId?: number,
    private billingEmailAddress?: string
  ) {
    // We assume that user entered the page not after the success payment, but directly
    if (!firstName) {
      this.goToDashboard();
    }
  }

  public api() {
    return {};
  }

  public data() {
    return {
      getShouldShowXeroPartner: () => this.xeroPartnerTurnedOn && !this.isSocialStarter && !this.isCurrentPlanEvents,
      getShouldShowCard: () => !this.isSocialStarter,
      getShouldShowResendVerification: () => this.isSocialStarter && !this.hasVerifiedEmail,
      getXeroPartnerContentForMoretonBay: () => {
        const content = [
          this.t('application-layer.thank-you-page.xero-partner-for-moreton-bay.content.first-paragraph'),
          this.t('application-layer.thank-you-page.xero-partner-for-moreton-bay.content.second-paragraph'),
          this.t('application-layer.thank-you-page.xero-partner-for-moreton-bay.content.third-paragraph'),
          [
            this.t('application-layer.thank-you-page.xero-partner-for-moreton-bay.content.list.item-1'),
            this.t('application-layer.thank-you-page.xero-partner-for-moreton-bay.content.list.item-2'),
            this.t('application-layer.thank-you-page.xero-partner-for-moreton-bay.content.list.item-3'),
          ],
        ];

        return {
          heading: this.t('application-layer.thank-you-page.xero-partner-for-moreton-bay.heading'),
          subheading: this.t('application-layer.thank-you-page.xero-partner-for-moreton-bay.subheading'),
          btnLabel: this.t('application-layer.thank-you-page.xero-partner-for-moreton-bay.btn-label'),
          content,
        };
      },
      getXeroPartnerContent: () => {
        const content = [
          this.t('application-layer.thank-you-page.xero-partner.content.first-paragraph'),
          this.t('application-layer.thank-you-page.xero-partner.content.second-paragraph'),
          [
            this.t('application-layer.thank-you-page.xero-partner.content.list.item-1'),
            this.t('application-layer.thank-you-page.xero-partner.content.list.item-2'),
            this.t('application-layer.thank-you-page.xero-partner.content.list.item-3'),
          ],
        ];

        return {
          heading: this.t('application-layer.thank-you-page.xero-partner.heading'),
          subheading: this.t('application-layer.thank-you-page.xero-partner.subheading'),
          btnLabel: this.t('application-layer.thank-you-page.xero-partner.btn-label'),
          content,
        };
      },
      getXeroPartnerContentWithAddonSelected: () => {
        const content = [
          this.t('application-layer.thank-you-page.xero-partner-with-addon.content.first-paragraph'),
          this.t('application-layer.thank-you-page.xero-partner-with-addon.content.second-paragraph'),
          this.t('application-layer.thank-you-page.xero-partner-with-addon.content.third-paragraph'),
          [
            this.t('application-layer.thank-you-page.xero-partner-with-addon.content.list.item-1'),
            this.t('application-layer.thank-you-page.xero-partner-with-addon.content.list.item-2'),
            this.t('application-layer.thank-you-page.xero-partner-with-addon.content.list.item-3'),
          ],
        ];

        return {
          heading: this.t('application-layer.thank-you-page.xero-partner-with-addon.heading'),
          subheading: this.t('application-layer.thank-you-page.xero-partner-with-addon.subheading'),
          btnLabel: this.t('application-layer.thank-you-page.xero-partner-with-addon.btn-label'),
          content,
        };
      },
      getHeading: () => {
        return this.t('application-layer.thank-you-page.heading', { name: this.firstName ?? '' });
      },
      getHeadingForMoretonBay: () => {
        return this.t('application-layer.thank-you-page.heading-for-moreton-bay', { name: this.firstName ?? '' });
      },
      getSubHeading: () => {
        if (this.isSocialStarter && !this.hasVerifiedEmail) {
          return this.t('application-layer.thank-you-page.subheading-unverified-email');
        }

        return this.t('application-layer.thank-you-page.subheading');
      },
      getSubHeadingForMoretonBay: () => this.t('application-layer.thank-you-page.subheading-for-moreton-bay'),
      getSubHeading2: () => this.t('application-layer.thank-you-page.subheading2'),
      getResendVerificationButtonLabel: () =>
        this.t('application-layer.thank-you-page.resend-verification-button-label'),
      getButtonLabel: (returnUrl?: string | null) =>
        returnUrl
          ? this.t('application-layer.thank-you-page.continue-button-label')
          : this.t('application-layer.thank-you-page.button-label'),
      getCardTitle: () => this.t('application-layer.thank-you-page.card-title'),
      getCardDescription: () => this.t('application-layer.thank-you-page.card-description'),
      getCardButtonLabel: () => this.t('application-layer.thank-you-page.card-button-label'),
      getSmsBundleColumnOne: () => ({
        heading: this.t('application-layer.thank-you-page.sms-bundle.column-one.heading'),
        line: this.t('application-layer.thank-you-page.sms-bundle.column-one.line'),
        stepsHeading: this.t('application-layer.thank-you-page.sms-bundle.column-one.steps-heading'),
        step1: this.t('application-layer.thank-you-page.sms-bundle.column-one.step1'),
        step2: this.t('application-layer.thank-you-page.sms-bundle.column-one.step2'),
        step3: this.t('application-layer.thank-you-page.sms-bundle.column-one.step3'),
      }),
      getSmsBundleRegisterSmsLabel: () => this.t('application-layer.thank-you-page.sms-bundle.column-one.register-sms'),
      getSmsBundleColumnTwo: () => ({
        heading: this.t('application-layer.thank-you-page.sms-bundle.column-two.heading'),
        line: this.t('application-layer.thank-you-page.sms-bundle.column-two.line'),
        goToDashboard: this.t('application-layer.thank-you-page.sms-bundle.column-two.go-to-dashboard'),
      }),
    };
  }

  public handlers() {
    return {
      onMainButtonClick: (returnUrl?: string | null) => {
        thankYouEventBus.emit('mainButtonClicked', { source: 'user' });

        if (returnUrl) {
          window.location.href = returnUrl;

          return;
        }

        this.goToDashboard();
      },
      onCardButtonClick: () => {
        thankYouEventBus.emit('cardButtonClicked', { source: 'user' });
        this.goToSettings();
      },
      onXeroPartnerButtonClick: async () => {
        thankYouEventBus.emit('xeroPartnerButtonClicked', { source: 'user' });
        this.goToXeroPartner();
      },
      onResendVerificationClick: () => {
        thankYouEventBus.emit('resendVerificationClicked', { source: 'user' });
        this.sendVerificationEmail();
      },
      onRegisterSmsClick: () => {
        thankYouEventBus.emit('registerSmsClicked', { source: 'user' });
        navigationApi.goToSmsSettings();
      },
    };
  }

  private goToDashboard() {
    navigationApi.goToDashboard();
  }

  private goToSettings() {
    navigationApi.goToSettings();
  }

  private goToXeroPartner() {
    const urls = {
      regular: 'https://xero5440.partnerlinks.io/ctct-post-purchase',
      'moreton-bay': 'https://xero5440.partnerlinks.io/moretonbay',
    } as const;

    const key = this.version === 'sms-bundle' ? 'regular' : this.version;
    const url = urls[key];

    window.open(url, '_blank');
  }

  private async sendVerificationEmail() {
    if (!this.billingEmailId) {
      console.error('No billing email ID available');

      return;
    }

    try {
      await accountRepo.sendVerificationEmail(this.billingEmailId);
      thankYouEventBus.emit('verificationEmailSent', this.billingEmailAddress ?? '', { source: 'api' });
    } catch (error) {
      console.error('Failed to send verification email:', error);
      thankYouEventBus.emit('verificationEmailFailed', { source: 'api' });
    }
  }
}
